import { apiClient } from "./authService";

export type UIInvoice = {
  id: number;
  type: "QUOTATION" | "INVOICE" | "CREDIT_NOTE" | "DEBIT_NOTE";

  amount: number;
  status:
    | "DRAFT"
    | "PENDING"
    | "PARTIALLY_PAID"
    | "PAID"
    | "REJECTED"
    | "APPROVED";
  createdAt: string;

  unit?: {
    id: string;
    name: string;
  };

  business?: {
    id: string;
    name: string;
  };

  client?: {
    id: string;
    name: string;
    phone?: string;
  };

  services?: {
    name: string;
    price: number;
  }[];

  notes?: string;
};

// GLOBAL LIST
export const getAllInvoices = async (params?: {
  status?: string;
  from?: string;
  to?: string;
  sourceReferenceId?: string;
  businessId?: string;
  unitId?: string;
  page?: number;
  size?: number;
  search?: string;
  /** Also require the invoice's creation date to fall in from/to. */
  matchCreatedDate?: string;
  /** Ask for totals over every matching invoice. */
  summary?: string;
}) => {
  const query = new URLSearchParams(
    params as Record<string, string>
  ).toString();

  const res = await apiClient.get(`/invoices/all${query ? `?${query}` : ""}`);
  return res.data;
};

// BY BUSINESS
export const getInvoicesByBusiness = async (
  businessId: string,
  params?: any
) => {
  const query = new URLSearchParams(params ?? {}).toString();

  const res = await apiClient.get(
    `/invoices/by-business/${businessId}${query ? `?${query}` : ""}`
  );
  return res.data;
};

// BY UNIT
export const getInvoicesByUnit = async (unitId: string, params?: any) => {
  const query = new URLSearchParams(params ?? {}).toString();

  const res = await apiClient.get(
    `/invoices/by-unit/${unitId}${query ? `?${query}` : ""}`
  );
  return res.data;
};

// BY USER (ADMIN VIEW)
export const getInvoicesForUser = async (clientUserId: string) => {
  const res = await apiClient.get(`/invoices/user/${clientUserId}`);
  return res.data;
};

// SINGLE
export const getInvoiceById = async (invoiceId: string) => {
  const res = await apiClient.get(`/invoices/${invoiceId}`);
  return res.data;
};

// CREATE QUOTATION (MAIN ENTRY POINT)
export const createQuotation = async (payload: {
  clientOrgUserId: string;
  currency: string;

  unitId?: string;
  businessId?: string;
  notes?: string;

  promoCode?: string;
  promoType?: "PERCENT" | "FLAT";
  promoValue?: number;

  items: {
    description: string;
    qty: number;
    unitPrice: number;
    discount?: number;
    metadata?: Record<string, any>;
  }[];
}) => {
  const res = await apiClient.post("/quotations", payload);
  return res.data;
};

// APPROVE QUOTATION → BECOMES INVOICE
export const approveQuotation = async (quotationId: number) => {
  const res = await apiClient.post(`/quotations/${quotationId}/approve`);
  return res.data;
};

// REJECT QUOTATION
export const rejectQuotation = async (quotationId: number) => {
  const res = await apiClient.post(`/quotations/${quotationId}/reject`);
  return res.data;
};

// LIST QUOTATIONS FOR USER
export const getQuotationsForUser = async (clientUserId: string) => {
  const res = await apiClient.get(`/quotations/user/${clientUserId}`);
  return res.data;
};


export interface PayInvoiceResponse {
  /** Set when the invoice had nothing left to pay; no payment was recorded. */
  alreadyPaid?: boolean;
  [key: string]: any;
}

// PAY (DEPOSIT – NOT REVENUE)
export const payInvoice = async (
  invoiceId: string,
  payload: {
    amount: number;
    method: "CARD" | "CASH" | "WALLET";
    provider: "STRIPE" | "TAP" | "PAYPAL" | "MANUAL";
    providerTransactionId?: string;
    status?: "SUCCESS" | "FAILED" | "PENDING";
  }
) => {
  const res = await apiClient.post<PayInvoiceResponse>(
    `/invoices/${invoiceId}/pay`,
    payload
  );
  return res.data;
};

export const refundInvoice = async (
  invoiceId: string,
  payload: {
    amount: number;
    paymentId?: number;
    reason?: string;
    status?: "SUCCESS" | "FAILED" | "PENDING";
  }
) => {
  const res = await apiClient.post(`/refunds/${invoiceId}`, payload);
  return res.data;
};

// CANCEL = CREDIT NOTE
export const cancelInvoice = async (
  invoiceId: string | number,
  payload: {
    amount: number;
    reason: string;
  }
) => {
  const res = await apiClient.post("/notes/credit", {
    invoiceId: Number(invoiceId),
    amount: payload.amount,
    reason: payload.reason,
  });

  return res.data;
};


export const updateQuotation = async (
  quotationId: number,
  payload: {
    userId: number;
    currency: string;

    promoCode?: string;
    promoType?: "PERCENT" | "FLAT";
    promoValue?: number;

    discount?: number;
    tax?: number;

    issuedAt?: string;
    dueAt?: string;

    sourceReferenceId?: string;
    metadata?: any;

    items: {
      description: string;
      qty: number;
      unitPrice: number;
      discount?: number;
      metadata?: any;
    }[];
  }
) => {
  const res = await apiClient.post(`/quotations/${quotationId}/update`, payload);
  return res.data;
};


export const getInvoiceByAppointmentId = async (appointmentId: string) => {
  try {
    const res = await apiClient.get<any>(`/invoices/appointment/${appointmentId}`);
    return res.data;
  } catch (error) {
    throw error;
  }
};
